import React from "react";
import { MessageSquare, Check, Calendar, UserCheck, UserCog, AtSign, RefreshCw, ArrowUp, Smile, Timer, Plus, FolderOpen, AlertCircle, ClipboardCheck, ClipboardX } from "lucide-react";
import { Notification, NotificationType, HrmNotificationType, NotificationOutcomeData } from "types/notifications";

// ─── Attendance / Regularization Notification Helpers ───────────────────────

// Notification types where the request is still pending and needs an HR Admin/Manager decision —
// these are the only ones that should open MissedCheckoutReviewModal (its API is admin-only).
// Everything else attendance-related (approved/rejected/recorded/superseded) is just an outcome
// update — show it via NotificationOutcomeModal using the notification's own content, no API call.
export const REVIEW_REQUIRED_NOTIFICATION_TYPES = ["MISSING_CHECKOUT_REVIEW_REQUIRED", "ATTENDANCE_REGULARIZATION_SUBMITTED"];

export const isAttendanceNotificationType = (typeStr: string, referenceType?: string | null): boolean =>
  typeStr.includes("ATTENDANCE") ||
  typeStr.includes("MISSING_CHECKOUT") ||
  typeStr === "ATTENDANCE_REGULARIZATION_SUBMITTED" ||
  referenceType === "ATTENDANCE_REGULARIZATION_REQUEST";

export const buildAttendanceOutcomeData = (typeStr: string, title: string, message: string): NotificationOutcomeData => ({
  title,
  message,
  status: typeStr.includes("REJECT") ? "error" : "success",
});


// ─── API Notification Helpers ────────────────────────────────────────────────────

export const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case "TASK_ASSIGNED": return UserCheck;
    case "TASK_ASSIGNEE_UPDATED": return UserCog;
    case "MENTIONED_IN_COMMENT":
    case "MENTIONED_IN_TASK": return AtSign;
    case "TASK_STATUS_UPDATED": return RefreshCw;
    case "TASK_PRIORITY_UPDATED": return ArrowUp;
    case "COMMENT_REACTION": return Smile;
    case "TIME_LOGGED": return Timer;
    case "WORK_ITEM_CREATED": return Plus;
    case "PROJECT_INVITE": return FolderOpen;
    default: return MessageSquare;
  }
};

export const getNotificationColors = (type: NotificationType) => {
  switch (type) {
    case "TASK_ASSIGNED":
    case "TASK_ASSIGNEE_UPDATED": return { bg: "bg-[#9B6DFF]/10", text: "text-[#9B6DFF]" };
    case "MENTIONED_IN_COMMENT":
    case "MENTIONED_IN_TASK": return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "TASK_STATUS_UPDATED": return { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" };
    case "TASK_PRIORITY_UPDATED": return { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]" };
    case "COMMENT_REACTION": return { bg: "bg-[#EC4899]/10", text: "text-[#EC4899]" };
    case "TIME_LOGGED": return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
    case "WORK_ITEM_CREATED": return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "PROJECT_INVITE": return { bg: "bg-[#9B6DFF]/10", text: "text-[#9B6DFF]" };
    default: return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
  }
};

export const getHrmNotificationIcon = (type: HrmNotificationType) => {
  switch (type) {
    case "MISSING_CHECKOUT_REVIEW_REQUIRED": return AlertCircle;
    case "MISSING_CHECKOUT_RECORDED": return ClipboardCheck;
    case "MISSING_CHECKOUT_APPROVED": return ClipboardCheck;
    case "MISSING_CHECKOUT_REJECTED": return ClipboardX;
    case "MISSING_CHECKOUT_SUPERSEDED": return RefreshCw;
    case "ONBOARDING_PROFILE_SUBMITTED": return UserCheck;
    case "ONBOARDING_PROFILE_RESUBMITTED": return RefreshCw;
    case "ONBOARDING_REWORK_REQUIRED": return AlertCircle;
    case "ONBOARDING_PROFILE_APPROVED": return UserCheck;
    case "NEW_TEAM_MEMBER_JOINED": return Smile;
    case "LEAVE_APPROVAL_SUPERSEDED": return RefreshCw;
    case "LEAVE_CANCELLATION_REVIEW_REQUIRED": return AlertCircle;
    case "LEAVE_CANCELLATION_REVIEW_SUPERSEDED": return RefreshCw;
    default: return MessageSquare;
  }
};

export const getHrmNotificationColors = (type: HrmNotificationType) => {
  switch (type) {
    case "MISSING_CHECKOUT_REVIEW_REQUIRED": return { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" };
    case "MISSING_CHECKOUT_RECORDED": return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
    case "MISSING_CHECKOUT_APPROVED": return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "MISSING_CHECKOUT_REJECTED": return { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]" };
    case "MISSING_CHECKOUT_SUPERSEDED": return { bg: "bg-[#9CA3AF]/10", text: "text-[#9CA3AF]" };
    case "ONBOARDING_PROFILE_SUBMITTED": return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
    case "ONBOARDING_PROFILE_RESUBMITTED": return { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" };
    case "ONBOARDING_REWORK_REQUIRED": return { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]" };
    case "ONBOARDING_PROFILE_APPROVED": return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "NEW_TEAM_MEMBER_JOINED": return { bg: "bg-[#9B6DFF]/10", text: "text-[#9B6DFF]" };
    case "LEAVE_APPROVAL_SUPERSEDED": return { bg: "bg-[#9CA3AF]/10", text: "text-[#9CA3AF]" };
    case "LEAVE_CANCELLATION_REVIEW_REQUIRED": return { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" };
    case "LEAVE_CANCELLATION_REVIEW_SUPERSEDED": return { bg: "bg-[#9CA3AF]/10", text: "text-[#9CA3AF]" };
    default: return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
  }
};

export const getDateGroup = (createdAt: string): "Today" | "Yesterday" | "Older" => {
  if (!createdAt) return "Older";
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) return "Older";
  const now = new Date();
  const todayStr = now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === todayStr) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return "Older";
};

export const formatNotificationTimestamp = (createdAt: string, group: "Today" | "Yesterday" | "Older"): string => {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) return "";

  if (group === "Today") {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours || 1}h ago`;
  }

  if (group === "Yesterday") {
    return "Yesterday";
  }

  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const NotificationMessageBody: React.FC<{ item: Notification }> = ({ item }) => {
  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (item.notificationType === "TASK_STATUS_UPDATED" && item.notificationMessage) {
    const statusRegex = /(.*) updated status of (.+?) from (.+) to (?!.* to )(.+)/i;
    const match = item.notificationMessage.match(statusRegex);
    if (match) {
      const oldStatus = match[3].trim();
      const newStatus = match[4].trim();

      const titleDisplay = item.title || match[2];

      return (
        <div className="flex flex-col gap-0.5">
          <p className="text-white text-[13px] font-semibold leading-[19.5px] hover:underline cursor-pointer break-words">
            {titleDisplay}
          </p>
          <p className="text-[#94a3b8] text-[13px] leading-[19.5px] break-words">
            Status changed by <span className="text-white font-medium hover:underline cursor-pointer">{item.actorName || match[1]}</span>
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="px-2 py-0.5 rounded-[4px] text-[10px] font-medium leading-[15px] align-middle bg-[#64748b]/10 text-[#cbd5e1] border border-[#64748b]/20">
              {toTitleCase(oldStatus)}
            </span>
            <span className="text-[#64748b]">&rarr;</span>
            <span className="px-2 py-0.5 rounded-[4px] text-[10px] font-medium leading-[15px] align-middle bg-[#3b82f6]/10 text-[#93c5fd] border border-[#3b82f6]/20">
              {toTitleCase(newStatus)}
            </span>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col gap-0.5">
      {item.title && (
        <p className="text-white text-[13px] font-semibold leading-[19.5px] hover:underline cursor-pointer break-words">
          {item.title}
        </p>
      )}
      <p className="text-[#94a3b8] text-[13px] leading-[19.5px] break-words">
        {item.notificationMessage}
      </p>
    </div>
  );
};

// ─── Mock Helpers (Can be removed later) ──────────────────────────────────────


export const getActorStyle = (initials: string) => {
  switch (initials) {
    case "SL":
      return { bg: "bg-[#4338ca]", text: "text-white" }; // Indigo
    case "MJ":
      return { bg: "bg-[#ea580c]", text: "text-white" }; // Orange/Red
    case "AM":
      return { bg: "bg-[#0d9488]", text: "text-white" }; // Teal
    case "RK":
      return { bg: "bg-[#7c3aed]", text: "text-white" }; // Purple
    case "TN":
      return { bg: "bg-[#d97706]", text: "text-white" }; // Amber
    case "LC":
      return { bg: "bg-[#0891b2]", text: "text-white" }; // Cyan
    case "S":
    default:
      return { bg: "bg-[#475569]", text: "text-white" }; // Slate/System
  }
};

export const getOverlayIcon = (category: string, action: string) => {
  if (category === "mention" || action.includes("comment") || action.includes("reply") || action.includes("mentioned")) {
    const isBlue = action.includes("commented") || action.includes("reply") || action.includes("Tom");
    return {
      bg: isBlue ? "bg-[#3b82f6]" : "bg-[#a855f7]",
      icon: <MessageSquare size={9} className="text-white fill-white" />
    };
  }
  if (category === "assigned" || action.includes("assigned")) {
    return {
      bg: "bg-[#10b981]",
      icon: <Check size={9} strokeWidth={3} className="text-white" />
    };
  }
  if (category === "update" || action.includes("status") || action.includes("marked")) {
    const isOrange = action.includes("marked") || action.includes("Done");
    return {
      bg: isOrange ? "bg-[#f97316]" : "bg-[#6366f1]",
      icon: <Check size={9} strokeWidth={3} className="text-white" />
    };
  }
  return {
    bg: "bg-[#9ca3af]",
    icon: <Calendar size={9} className="text-white" />
  };
};

export const getTagStyle = (tag: string) => {
  switch (tag.toLowerCase()) {
    case "frontend":
      return "bg-[#8b5cf6]/10 text-[#a78bfa] border border-[#8b5cf6]/20";
    case "design":
      return "bg-[#f97316]/10 text-[#fdba74] border border-[#f97316]/20";
    case "crm":
      return "bg-[#3b82f6]/10 text-[#93c5fd] border border-[#3b82f6]/20";
    case "system":
    default:
      return "bg-[#64748b]/10 text-[#cbd5e1] border border-[#64748b]/20";
  }
};

export const sanitizeDeepLink = (deepLink?: string, currentOrgSlug?: string): string | null => {
  if (!deepLink) return null;

  let cleanPath = deepLink.trim();
  if (!cleanPath.startsWith('/')) {
    cleanPath = `/${cleanPath}`;
  }

  if (currentOrgSlug) {
    const parts = cleanPath.split('/').filter(Boolean);
    if (parts.length > 0) {
      if (parts[0] !== currentOrgSlug) {
        if (['hrms', 'pmo', 'crm', 'finance', 'overview', 'settings', 'account-setup'].includes(parts[0])) {
          cleanPath = `/${currentOrgSlug}/${parts.join('/')}`;
        } else {
          parts[0] = currentOrgSlug;
          cleanPath = `/${parts.join('/')}`;
        }
      }
    }
  }

  return cleanPath;
};

